import { Brick, BrickContext, ErrorFlow, registerBrick, Transaction, Query, Predicate } from 'olympe';
import Unit from '../../../models/Unit';
import Observation from '../../../models/Observation';
import Inspection from "../../../models/Inspection";
import Component from "../../../models/Component";
import System from "../../../models/System";
import InspectionEntity from "../../../models/InspectionEntity";

export default class CreateMissingObservations extends Brick {
    /**
     * @protected
     * @param {!BrickContext} $
     * @param {Unit} unit
     * @param {!Array} inspectionEntities
     * @param {!boolean} onlyAvailable
     * @param {function()} forwardEvent
     * @param {function(*)} setErrorFlow
     */
    update($, [unit, inspectionEntities = [], onlyAvailable = true], [forwardEvent, setErrorFlow]) {
        if (!(unit instanceof Unit)) {
            setErrorFlow(ErrorFlow.create(`Try to create observations for a Unit which is not a Unit object: ${unit}`, 1));
            return;
        }

        // Build the predicate to filter out unavailable entities
        const unitStates = Query.from(unit).follow(Unit.statesRel).executeFromCache();

        // Get all the entities that already have an observation in this unit to avoid duplicate
        const entitiesWithObservation = new Set(Query.from(unit)
            .follow(Unit.observationsRel)
            .follow(Observation.targetRel)
            .executeFromCache().keys());

        const transaction = new Transaction(true);

        const observationsToCreate = new Set(inspectionEntities.flatMap((entity) => {
            // Do not create observations for entities that have no available inspection
            const children = getInspectionEntityChildren(entity, onlyAvailable, Predicate.in(...unitStates.values()));
            const parents = this.getParents(entity);
            return children.length > 0 ? children.concat(parents.slice(1)) : [];
        }).map((entity) => {
            return entity.getTag();
        }).filter((entity) => {
            // Do not create observations for entities that already have one.
            return !entitiesWithObservation.has(entity);
        }));

        observationsToCreate.forEach((entity) => {
            this.createObservation(transaction, unit, entity);
        });

        transaction.executeAsLarge().then(forwardEvent).catch((e) => {
            setErrorFlow(ErrorFlow.create(`Failed to create observations: ${e}`, 0));
        });
    }

    /**
     * @private
     * @param {InspectionEntity} entity
     * @return {Array<InspectionEntity>} entity
     */
    getParents(entity) {
        if (entity === null) {
            return [];
        }

        if (entity instanceof Inspection) {
            return [entity].concat(this.getParents(entity.followSingle(Component.inspectionsRel.getInverse()).executeFromCache()))
        }

        else if (entity instanceof Component) {
            return [entity].concat(
                this.getParents(entity.followSingle(Component.subComponentsRel.getInverse()).executeFromCache()),
                this.getParents(entity.followSingle(System.componentsRel.getInverse()).executeFromCache())
            );
        }

        return [entity];
    }

    /**
     * @private
     * @param {Transaction} transaction
     * @param {Unit} unit
     * @param {InspectionEntity} entity
     */
    createObservation(transaction, unit, entity) {
        const obs = transaction.create(Observation, new Map([[Observation.commentProp, '']]));
        transaction.createRelation(Observation.targetRel, obs, entity);
        transaction.createRelation(Unit.observationsRel, unit, obs);
    }
}

/**
 * Return the flattened list of children in the tree, starting at the specified entity.
 *
 * @param {InspectionEntity} entity
 * @param {boolean} filterAvailable
 * @param {?Predicate} predicate
 * @return {InspectionEntity[]}
 */
export const getInspectionEntityChildren = (entity, filterAvailable, predicate) => {
    if (entity instanceof System) {
        const components = entity.follow(System.componentsRel).executeFromCache().toArray();
        const children = components.flatMap((component) => getInspectionEntityChildren(component, filterAvailable, predicate));
        return [entity].concat(children);
    }

    else if (entity instanceof Component) {
        const subcomponents = entity.follow(Component.subComponentsRel).executeFromCache().toArray();
        let subEntities;
        if (subcomponents.length > 0) {
            subEntities = subcomponents.flatMap((subcomponent) => getInspectionEntityChildren(subcomponent, filterAvailable, predicate));
        }
        // No subcomponents => It is a leaf component so get inspections.
        else {
            const inspections = entity.follow(Component.inspectionsRel).executeFromCache().toArray();
            subEntities = inspections.flatMap((inspection) => getInspectionEntityChildren(inspection, filterAvailable, predicate));
        }
        // Exclude the component if none of its sub entity is available.
        return subEntities.length > 0 ? [entity].concat(subEntities) : [];
    }

    else if (entity instanceof Inspection) {
        if (filterAvailable && predicate) {
            const states = entity.follow(InspectionEntity.presumedStatesRel).filter(predicate).executeFromCache();
            return states.size() > 0 ? [entity] : [];
        }
        return [entity];
    }

    return [];
}

registerBrick('0189736c88c017534d10', CreateMissingObservations);
